import { useId } from "react";
import type { InputHTMLAttributes, ReactNode, Ref, TextareaHTMLAttributes } from "react";
import { classes } from "../../nucleo/classes";

interface BaseDoCampo {
  rotulo: string;
  ajuda?: ReactNode;
  erro?: string;
  obrigatorio?: boolean;
}

function Moldura({
  rotulo,
  ajuda,
  erro,
  obrigatorio,
  idDoControle,
  idDaAjuda,
  idDoErro,
  className,
  children,
}: BaseDoCampo & {
  idDoControle: string;
  idDaAjuda: string;
  idDoErro: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={classes("campo", className)}>
      <label className="campo__rotulo" htmlFor={idDoControle}>
        {rotulo}
        {obrigatorio ? (
          <span className="campo__obrigatorio" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children}
      {ajuda ? (
        <p className="campo__ajuda" id={idDaAjuda}>
          {ajuda}
        </p>
      ) : null}
      {erro ? (
        <p className="campo__erro" id={idDoErro} role="alert">
          {erro}
        </p>
      ) : null}
    </div>
  );
}

/** Liga o controle à ajuda e ao erro para leitor de tela — e só ao que existe na tela. */
function descrito(ajuda: ReactNode, erro: string | undefined, idDaAjuda: string, idDoErro: string) {
  const ids = [ajuda ? idDaAjuda : null, erro ? idDoErro : null].filter(Boolean);
  return ids.length > 0 ? ids.join(" ") : undefined;
}

export interface PropsDoCampo
  extends BaseDoCampo,
    Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  ref?: Ref<HTMLInputElement>;
  classeDaMoldura?: string;
}

export function Campo({
  rotulo,
  ajuda,
  erro,
  obrigatorio,
  classeDaMoldura,
  className,
  required,
  ...resto
}: PropsDoCampo) {
  const base = useId();
  const idDoControle = `${base}-controle`;
  const idDaAjuda = `${base}-ajuda`;
  const idDoErro = `${base}-erro`;

  return (
    <Moldura
      rotulo={rotulo}
      ajuda={ajuda}
      erro={erro}
      obrigatorio={obrigatorio ?? required}
      idDoControle={idDoControle}
      idDaAjuda={idDaAjuda}
      idDoErro={idDoErro}
      className={classeDaMoldura}
    >
      <input
        id={idDoControle}
        className={classes("campo__controle", className)}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descrito(ajuda, erro, idDaAjuda, idDoErro)}
        required={required}
        {...resto}
      />
    </Moldura>
  );
}

export interface PropsDoCampoLongo
  extends BaseDoCampo,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  ref?: Ref<HTMLTextAreaElement>;
  classeDaMoldura?: string;
}

export function CampoLongo({
  rotulo,
  ajuda,
  erro,
  obrigatorio,
  classeDaMoldura,
  className,
  required,
  ...resto
}: PropsDoCampoLongo) {
  const base = useId();
  const idDoControle = `${base}-controle`;
  const idDaAjuda = `${base}-ajuda`;
  const idDoErro = `${base}-erro`;

  return (
    <Moldura
      rotulo={rotulo}
      ajuda={ajuda}
      erro={erro}
      obrigatorio={obrigatorio ?? required}
      idDoControle={idDoControle}
      idDaAjuda={idDaAjuda}
      idDoErro={idDoErro}
      className={classeDaMoldura}
    >
      <textarea
        id={idDoControle}
        className={classes("campo__controle", className)}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descrito(ajuda, erro, idDaAjuda, idDoErro)}
        required={required}
        {...resto}
      />
    </Moldura>
  );
}

